import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { SheetPickerList, type SheetPickerOption } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import axios from '@/lib/axios';

interface BoardPerson {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    role?: string | null;
}

interface BoardAccessResponse {
    people: BoardPerson[];
    shared_user_ids: string[];
    role_holders: BoardPerson[];
}

interface ShareBoardModalProps {
    /** The board key, e.g. "workorders". */
    board: string;
    /** Human label for the board, shown in the sheet title. */
    boardLabel: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Gives named people access to a pre-defined analytics board.
 *
 * Same shape as the Imitate sheet — a searchable list in the sheet body grouped
 * by role, rather than a combobox opening a second layer over the overlay.
 *
 * A fixed board is governed by a permission, so this grants that permission
 * directly. People who already hold the board through their role are listed as
 * disabled rows: taking it from one of them means a denial, which belongs in the
 * employee's own Permissions tab.
 */
export default function ShareBoardModal({ board, boardLabel, open, onOpenChange }: ShareBoardModalProps) {
    const { t } = useTranslations();
    const [access, setAccess] = useState<BoardAccessResponse | null>(null);
    const [sharedIds, setSharedIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    // Fetched once per opening; the picker filters the list without a round trip.
    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;

        void (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get<BoardAccessResponse>(`/api/analytics/boards/${board}/access`);
                if (cancelled) {
                    return;
                }
                setAccess(data);
                setSharedIds(data.shared_user_ids);
            } catch {
                if (!cancelled) {
                    toast.error(t('analytics.board_sharing.load_failed', 'Could not load who has access to this board.'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, board, t]);

    /**
     * One heading per role, the roleless last, and everyone who already has the
     * board through their role in a closing group they cannot be removed from.
     */
    const options = useMemo<SheetPickerOption[]>(() => {
        const noRole = t('analytics.board_sharing.no_role', 'No role');
        const viaRoleGroup = t('analytics.board_sharing.via_role_group', 'Already have it through their role');
        const nameOf = (person: BoardPerson) =>
            [person.first_name, person.last_name].filter(Boolean).join(' ').trim() ||
            person.email ||
            t('analytics.board_sharing.unnamed', 'Unnamed user');

        const toOption = (person: BoardPerson, group: string, disabled = false): SheetPickerOption => ({
            id: person.id,
            label: nameOf(person),
            description: person.email ?? undefined,
            keywords: person.role ?? undefined,
            badge: person.role ? <Badge variant="secondary">{person.role}</Badge> : null,
            group,
            disabled,
        });

        const selectable = [...(access?.people ?? [])]
            .map((person) => ({ person, group: person.role?.trim() ? person.role.trim() : noRole }))
            .sort((a, b) => {
                if (a.group === b.group) {
                    return 0;
                }
                // The roleless bucket is a fallback, not a role — it goes last.
                if (a.group === noRole) {
                    return 1;
                }
                if (b.group === noRole) {
                    return -1;
                }
                return a.group.localeCompare(b.group);
            })
            .map(({ person, group }) => toOption(person, group));

        return [...selectable, ...(access?.role_holders ?? []).map((person) => toOption(person, viaRoleGroup, true))];
    }, [access, t]);

    // One add or one remove per toggle — a grant and a revoke firing together
    // would race on the same permission cache.
    const syncAccess = (next: string[]) => {
        const added = next.find((id) => !sharedIds.includes(id));
        const removed = sharedIds.find((id) => !next.includes(id));

        const request = added
            ? axios.post(`/api/analytics/boards/${board}/access`, { user_id: added })
            : removed
              ? axios.delete(`/api/analytics/boards/${board}/access/${removed}`)
              : null;

        if (!request) {
            return;
        }

        const previous = sharedIds;
        setSharedIds(next);

        request
            .then(({ data }) => {
                setAccess(data);
                setSharedIds(data.shared_user_ids ?? next);
                toast.success(
                    added ? t('analytics.board_sharing.granted', 'Access granted.') : t('analytics.board_sharing.revoked', 'Access removed.'),
                );
            })
            .catch(() => {
                setSharedIds(previous);
                toast.error(t('analytics.board_sharing.failed', 'Could not update access to this board.'));
            });
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('analytics.board_sharing.title', 'Share :board').replace(':board', boardLabel)}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'analytics.board_sharing.description',
                            'People you add here get this board — and the analytics page itself — granted to them directly. It shows up on their Permissions tab, so you can review it there later.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody>
                    <SheetPickerList
                        multiple
                        values={sharedIds}
                        onValuesChange={syncAccess}
                        options={options}
                        loading={loading && options.length === 0}
                        labels={{
                            search: t('analytics.board_sharing.search', 'Search people by name or email…'),
                            empty: t('analytics.board_sharing.empty', 'No people found.'),
                        }}
                    />
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('common.close', 'Close')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
